import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Button,
} from 'reactstrap';
import classnames from 'classnames';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import BreadCrumb from '../../../../Components/Common/BreadCrumb';
import ConfirmModal from '../../../../Components/crude/ConfirmModal';
import * as api from './invitationExternalApi';
import {
  INVITATION_STATUS,
  INVITATION_STATUS_BADGE,
  canRespond,
} from './invitationExternalConstants';
import { formatDateTime } from '../formatters';

import InvitationTab from './components/InvitationTab';
import SupportingDocumentTab from './components/SupportingDocumentTab';

// Invitation - External (DMUT) — Detail page.
//
// Reached from the list's View Detail action. All four statuses open this same
// page; status decides only whether Accept / Decline are offered (Open) — nothing
// else on the page is editable, ever.
//
// Accept  -> confirmation popup, then status becomes Accepted (and the downstream
//            Offer opens — see invitationExternalStore.accept).
// Decline -> confirmation popup with a MANDATORY reason; submit is refused while
//            the reason is blank.
const TABS = [
  { id: 1, icon: 'ri-mail-open-line', label: 'INVITATION' },
  { id: 2, icon: 'ri-file-text-line', label: 'SUPPORTING DOCUMENT' },
];

const InvitationExternalDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [acceptModal, setAcceptModal] = useState(false);
  const [declineModal, setDeclineModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const rec = await api.getInvitation(id);
      setDetail(rec);
      setNotFound(false);
    } catch (error) {
      if (String(error).includes('not found')) {
        setNotFound(true);
        setDetail(null);
      } else {
        toast.error(`Failed to load invitation: ${error}`);
      }
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  document.title = detail
    ? `Invitation - ${detail.procId}`
    : 'Invitation - External';

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await api.acceptInvitation(id);
      setAcceptModal(false);
      toast.success('Invitation accepted');
      await load();
    } catch (error) {
      toast.error(`Failed to accept: ${error}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ConfirmModal hands the notes value to onConfirm. The reason is mandatory
  // here, so an empty one is rejected and the dialog stays open for a retry
  // (the store enforces the same rule, so this cannot be bypassed).
  const handleDecline = async reason => {
    if (!String(reason || '').trim()) {
      toast.error('Please provide a reason for declining');
      return;
    }
    setSubmitting(true);
    try {
      await api.declineInvitation(id, reason);
      setDeclineModal(false);
      toast.success('Invitation declined');
      await load();
    } catch (error) {
      toast.error(`Failed to decline: ${error}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5'>
            <i className='ri-error-warning-line fs-1 text-muted'></i>
            <h5 className='mt-2'>Invitation not found</h5>
            <button
              className='btn btn-primary btn-sm mt-2'
              onClick={() => navigate('/crude/invitation-external')}
            >
              Back to list
            </button>
          </div>
        </Container>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5 text-muted'>Loading...</div>
        </Container>
      </div>
    );
  }

  const showActions = canRespond(detail.invitationStatus);

  const infoCards = [
    { label: 'Procurement No', value: detail.procId, icon: 'ri-draft-line' },
    {
      label: 'Procurement Type',
      value: detail.procurementType,
      icon: 'ri-contrast-drop-2-line',
    },
    {
      label: 'Procurement Method',
      value: detail.procurementMethod,
      icon: 'ri-price-tag-2-line',
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb
            title='Invitation Detail'
            pageTitle='Plan & Procurement'
          />

          {/* ---- Status strip + Accept / Decline (top right, Open only) ---- */}
          <Row className='mb-3 align-items-center g-2'>
            <Col>
              <div className='d-flex align-items-center gap-2 flex-wrap'>
                <span className='text-muted fs-13'>Invitation Status:</span>
                <Badge
                  color={
                    INVITATION_STATUS_BADGE[detail.invitationStatus] ||
                    'secondary'
                  }
                  className='px-2 fs-12'
                >
                  {detail.invitationStatus}
                </Badge>
                {detail.respondedAt && (
                  <span className='text-muted fs-12'>
                    responded {formatDateTime(detail.respondedAt)}
                  </span>
                )}
              </div>
            </Col>
            {showActions && (
              <Col xs='auto'>
                <div className='d-flex gap-2'>
                  <Button
                    color='success'
                    size='sm'
                    disabled={submitting}
                    onClick={() => setAcceptModal(true)}
                  >
                    <i className='ri-check-line align-bottom me-1'></i>
                    Accept
                  </Button>
                  <Button
                    color='danger'
                    size='sm'
                    outline
                    disabled={submitting}
                    onClick={() => setDeclineModal(true)}
                  >
                    <i className='ri-close-line align-bottom me-1'></i>
                    Decline
                  </Button>
                </div>
              </Col>
            )}
          </Row>

          {/* Why the actions are gone, when they are gone. */}
          {!showActions && (
            <div className='alert alert-light border d-flex align-items-start'>
              <i className='ri-information-line fs-16 me-2 mt-1'></i>
              <div>
                {detail.invitationStatus === INVITATION_STATUS.EXPIRED
                  ? 'This invitation expired without a response — the submission deadline has passed.'
                  : `This invitation has already been ${detail.invitationStatus.toLowerCase()}.`}
                {detail.declineReason && (
                  <div className='mt-1'>
                    <span className='fw-semibold'>Decline reason:</span>{' '}
                    {detail.declineReason}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                {/* Info cards */}
                <Row className='mt-0 mb-3 g-3 d-flex align-items-stretch'>
                  {infoCards.map(c => (
                    <Col xl={4} md={6} className='mb-0' key={c.label}>
                      <div className='card bg-soft-primary border shadow-none h-100 mb-0'>
                        <div className='card-body py-3'>
                          <div className='d-flex'>
                            <div
                              className='d-flex me-3'
                              style={{ width: '100%' }}
                            >
                              <div className='flex-shrink-0 me-3'>
                                <i
                                  className={`${c.icon} fs-2 text-warning`}
                                ></i>
                              </div>
                              <div>
                                <h6 className='mb-1 fw-bold'>{c.label}</h6>
                                <h5 className='mb-0'>{c.value || '-'}</h5>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>

                <Row className='justify-content-center mb-4'>
                  <Col xxl={12}>
                    <CardBody className='checkout-tab'>
                      <div className='mx-n4 px-4 message-list-scroll'>
                        <Nav
                          tabs
                          className='nav-tabs-custom nav-primary mt-1 mb-3'
                        >
                          {TABS.map(tab => (
                            <NavItem key={tab.id}>
                              <NavLink
                                className={classnames('cursor-pointer', {
                                  active: activeTab === tab.id,
                                })}
                                onClick={() => setActiveTab(tab.id)}
                              >
                                <i
                                  className={`${tab.icon} align-middle me-1`}
                                ></i>
                                <span className='fw-semibold'>{tab.label}</span>
                              </NavLink>
                            </NavItem>
                          ))}
                        </Nav>
                      </div>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId={1}>
                          {activeTab === 1 && <InvitationTab detail={detail} />}
                        </TabPane>
                        <TabPane tabId={2}>
                          {activeTab === 2 && (
                            <SupportingDocumentTab
                              invitationId={id}
                              detail={detail}
                            />
                          )}
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Col>
                </Row>
              </div>
            </div>
          </div>
        </Container>

        <ConfirmModal
          show={acceptModal}
          title='Accept Invitation'
          message='Accepting this invitation confirms your intention to participate in this tender. Continue?'
          confirmLabel='Accept'
          confirmColor='success'
          onConfirm={handleAccept}
          onClose={() => setAcceptModal(false)}
        />
        <ConfirmModal
          show={declineModal}
          title='Decline Invitation'
          message='Declining this invitation withdraws you from this tender. Please state your reason.'
          confirmLabel='Decline'
          confirmColor='danger'
          showNotes
          notesLabel='Reason for declining'
          notesPlaceholder='Required — recorded against this invitation'
          onConfirm={handleDecline}
          onClose={() => setDeclineModal(false)}
        />
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </React.Fragment>
  );
};

export default InvitationExternalDetailView;
